/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import { BuilderPermission, DrawRuleBasis } from '@gh/builder-shared-data';
import {
  FormStateSnapshot,
  getFormStateSnapshotValue,
  getInvalidFields,
  isEntityInvalid,
  markFormStateAsTouched,
  PlainImmutablePageState,
  setEntityField,
  setEntityFields,
  UserContext,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { MessageBasket } from '@gh/core-messages';
import { FormMode } from '@gh/core-ui';
import {
  differentFieldsWith,
  isNotNil,
  isNumberEquals,
  zeroNumberAsUndefined,
} from '@gh/core-util';
import { map$ } from '@gh/rx/operators';

import { find, first, isEmpty, isNil, keyBy } from 'lodash';

import {
  complementDrawRuleStage,
  createDrawRuleStage,
  DRAW_RULE_DETAILS_INIT,
  DrawRuleCpCc,
  DrawRuleCpStage,
  DrawRuleDetails,
  DrawRuleDetailsSelection,
  DrawRuleMasterInfo,
  DrawRuleStage,
  drawRuleStageArr,
  getDrawRuleStageId,
  isDrawRuleStageHasEmptyParagraph,
  isDrawRuleStagePctOrAmountField,
  mergeDrawRuleStageFields,
  validateDrawRuleMasterInfo,
} from '../../resources/draw-rule-details.model';

@Injectable()
export class DrawRuleDetailsFormState extends PlainImmutablePageState<DrawRuleDetails> {
  masterInfo$ = this.select((state) => state.masterInfo);
  currentStage$ = this.select(({ selection: { stageId }, stages }) =>
    stageId ? find(stages, { drawRuleStageId: stageId }) : void 0);
  currentStageId$ = this.currentStage$.pipe(map$((stage) => stage ? getDrawRuleStageId(stage) : void 0));
  focusedCell$ = this.select(({ selection }) => selection.focusedCell);
  isStageSelected$ = this.currentStage$.pipe(map$(Boolean));
  currentMemo$ = this.currentStage$.pipe(map$((stage) => stage ? stage.memo : ''));
  basis$ = this.masterInfo$.pipe(
    map$(getFormStateSnapshotValue),
    map$((masterInfo) => masterInfo.basis));
  code$ = this.masterInfo$.pipe(
    map$(getFormStateSnapshotValue),
    map$((masterInfo) => masterInfo.code));
  isCriticalPathBasis$ = this.basis$.pipe(map$((basis) => basis === DrawRuleBasis.CriticalPath));
  isCostCenterBasis$ = this.basis$.pipe(map$((basis) => basis === DrawRuleBasis.CostCenter));
  stages$ = this.select((state) => state.stages.filter((stage) => stage.status !== 0));
  cpStages$ = this.select((state) => state.criticalPathStages);
  cpCostCenters$ = this.select((state) => state.criticalPathCostCenters);
  currentParagraphInvalid$ = this.currentStage$.pipe(map$((stage) => stage ? isDrawRuleStageHasEmptyParagraph(stage) : false));

  constructor(private userContext: UserContext) {
    super(DRAW_RULE_DETAILS_INIT);
  }

  get drawRuleId(): ID {
    return this.value.drawRuleId;
  }

  get selection(): DrawRuleDetailsSelection {
    return this.value.selection;
  }

  get basis(): DrawRuleBasis {
    return getFormStateSnapshotValue(this.value.masterInfo).basis;
  }

  get stages(): DrawRuleStage[] {
    return this.value.stages;
  }

  get invalid(): boolean {
    return this.isMasterInfoInvalid
      || this.hasInvalidStage
      || this.hasSeveralDeposits || this.isDeductDepositInvalid || this.isSameStageAsDepositAndDeduct
      || this.hasEmptyParagraph
      || this.isTotalPercentsInvalid || this.isTotalBudgetInvalid
      || this.isOrderInvalid;
  }

  get isMasterInfoInvalid(): boolean {
    return isNotNil(validateDrawRuleMasterInfo(getFormStateSnapshotValue(this.value.masterInfo)));
  }

  get hasInvalidStage(): boolean {
    return this.value.stages.some(isEntityInvalid);
  }

  get hasSeveralDeposits(): boolean {
    return this.value.stages.filter((stage) => stage.isDepositStage).length > 1;
  }

  get isDeductDepositInvalid(): boolean {
    const deposits = this.value.stages.filter((stage) => stage.isDepositStage);
    const deductDeposits = this.value.stages.filter((stage) => stage.isDeductDeposit);
    // number of deposits and deduct deposits should be the same
    return deposits.length !== deductDeposits.length;
  }

  get isSameStageAsDepositAndDeduct(): boolean {
    const deposit = first(this.value.stages.filter((stage) => stage.isDepositStage));
    const deductDeposit = first(this.value.stages.filter((stage) => stage.isDeductDeposit));
    return deposit && deductDeposit ? deposit === deductDeposit : false;
  }

  get hasEmptyParagraph(): boolean {
    return this.value.stages.some(isDrawRuleStageHasEmptyParagraph);
  }

  get isTotalPercentsInvalid(): boolean {
    return !isNumberEquals(
      this.value.stages
        .filter((stage) => stage.isDepositStage || stage.status !== 0)
        .reduce((total, stage) => total + (stage.pctDue || 0), 0),
      100);
  }

  get isTotalBudgetInvalid(): boolean {
    return !isNumberEquals(this.value.stages
                             .filter((stage) => stage.status !== 0)
                             .reduce((total, stage) => total + (stage.jobBudgetPct || 0), 0), 100);
  }

  get isOrderInvalid(): boolean {
    const { basis } = this;
    const getOrderingValue: TransformFn<DrawRuleStage, any> = basis === DrawRuleBasis.CriticalPath
      ? (stage: DrawRuleStage) => stage.cpStage
      : (stage: DrawRuleStage) => stage.cpCostCenter;
    const getOrderByValue = basis === DrawRuleBasis.CriticalPath
      ? (stage: DrawRuleCpStage) => stage.sortOrder
      : (cc: DrawRuleCpCc) => cc.sortOrder;
    const compareOrders = basis === DrawRuleBasis.CriticalPath
      ? (a: number, b: number) => a < b
      : (a: number, b: number) => a <= b;
    return this.value.stages
      .map(getOrderingValue)
      .filter(Boolean)
      .map(getOrderByValue)
      .some((order, i, orders) => i === 0 ? false : !compareOrders(orders[i - 1], order));
  }

  build(details: DrawRuleDetails): void {
    if (isNil(details.drawRuleId)) {
      this.setMode(FormMode.CreateMode);
    } else if (this.userContext.hasPermission(BuilderPermission.Setup.Accounting.DrawRules.EDIT)) {
      this.setMode(FormMode.EditMode);
    } else {
      this.setMode(FormMode.ViewMode);
    }

    this.setValue(details);
    this.resetDirty();
  }

  setMasterInfo(newMasterInfo: FormStateSnapshot<DrawRuleMasterInfo>): void {
    const oldMasterInfo = this.value.masterInfo;
    const differentFields = differentFieldsWith(
      getFormStateSnapshotValue(newMasterInfo),
      getFormStateSnapshotValue(oldMasterInfo));

    this.setField('masterInfo', newMasterInfo);
    // if basis has been changed => clear critical path or/and cost center fields
    if (differentFields.includes('basis')) {
      this.updateStages((stages) => stages.map(mergeDrawRuleStageFields({
        cpStage: void 0,
        cpCostCenter: void 0,
      })));
    }
    this.setDirty(true);
  }

  highlightMasterInfo(): void {
    this.setField('masterInfo', markFormStateAsTouched(this.value.masterInfo));
  }

  navigateToInvalidStage(): void {
    const invalidStage = first(this.value.stages.filter(isEntityInvalid));
    const invalidField = invalidStage ? first(getInvalidFields(invalidStage)) : void 0;

    this.updateSelection((selection) =>
      invalidStage && invalidField
        ? setEntityField(selection, 'focusedCell', { id: getDrawRuleStageId(invalidStage), field: invalidField })
        : selection);
  }

  navigateToStageHavingEmptyParagraph(): void {
    const stageHavingEmptyParagraph = first(this.value.stages.filter(isDrawRuleStageHasEmptyParagraph));

    this.updateSelection((selection) =>
      stageHavingEmptyParagraph
        ? setEntityField(selection, 'stageId', getDrawRuleStageId(stageHavingEmptyParagraph))
        : selection);
  }

  setCurrentStageMemo(memo: string): void {
    const { stageId } = this.selection;
    if (stageId) {
      this.updateStages((stages) => drawRuleStageArr.merge(stages, stageId, mergeDrawRuleStageFields({ memo })));
      this.setDirty();
    }
  }

  setCurrentStage(stage?: DrawRuleStage): void {
    this.updateSelection((selection) => setEntityField(
      selection,
      'stageId',
      stage ? getDrawRuleStageId(stage) : void 0));
  }

  resetFocusedCell(): void {
    this.updateSelection((selection) => setEntityField(selection, 'focusedCell', void 0));
  }

  addStage(): void {
    this.updateStages((stages) => drawRuleStageArr.append(stages, createDrawRuleStage(stages.length + 1)));
    this.setDirty();
  }

  removeStage(stageToBeRemoved: DrawRuleStage): void {
    const deletedStageId = getDrawRuleStageId(stageToBeRemoved);

    this.updateStages((stages) => {
      // remove stage
      const withRemovedStage = drawRuleStageArr.remove(stages, deletedStageId);
      // renumber stages
      return withRemovedStage.map((stage, i) => setEntityField(stage, 'stageNumber', i + 1));
    });
    // reset selected stageId if deleted stage is selected now
    this.updateSelection((selection) =>
      selection.stageId === deletedStageId
        ? setEntityField(selection, 'stageId', void 0)
        : selection);
    this.setDirty();
  }

  markDeletedStage(stageToBeRemoved: DrawRuleStage): void {
    this.stageSetField(stageToBeRemoved, 'status', 0);
    this.stageSetField(stageToBeRemoved, 'isDepositStage', false);
    this.stageSetField(stageToBeRemoved, 'isDeductDeposit', false);
    this.showDeletedStages(true);
  }

  showDeletedStages(value: boolean): void {
    this.stages$ = this.select((state) => value
      ? state.stages
      : state.stages.filter((stage) => stage.status !== 0));
  }

  undeleteStage(stageToBeUndelete: DrawRuleStage): void {
    this.stageSetField(stageToBeUndelete, 'status', 1);
  }

  stageSetField(stage: DrawRuleStage, name: string, value: any): void {
    this.updateStages((stages) => drawRuleStageArr.merge(
      stages,
      getDrawRuleStageId(stage),
      mergeDrawRuleStageFields({
        // treat zero pctDue/amountDue as void 0
        [name]: isDrawRuleStagePctOrAmountField(name) ? zeroNumberAsUndefined(value) : value,
      })));
    this.setDirty();
  }

  setStageErrors(basket: MessageBasket): void {
    // prepare list of deleted critical paths and cost centers for fast searching
    const deletedCriticalPathIndex = keyBy(
      basket.messages.filter((msg) => msg.key === 'msg_critical_path_deleted'),
      (msg) => msg.params['criticalPathId']);
    const deletedCostCenterIndex = keyBy(
      basket.messages.filter((msg) => msg.key === 'msg_critical_path_cc_deleted'),
      (msg) => msg.params['costCenterId']);

    if (isEmpty(deletedCriticalPathIndex) && isEmpty(deletedCostCenterIndex)) {
      return;
    }

    // mark deleted cost centers and stages as deleted
    this.updateStages((stages) => drawRuleStageArr.updateAll(
      stages,
      (stage) => complementDrawRuleStage(setEntityFields(stage, {
        cpCostCenter: stage.cpCostCenter
          ? setEntityFields(stage.cpCostCenter, {
            isCriticalPathDeleted: isNotNil(deletedCriticalPathIndex[stage.cpCostCenter.criticalPathId]),
            isCostCenterDeleted: isNotNil(deletedCostCenterIndex[stage.cpCostCenter.costCenterId]),
          })
          : void 0,
        cpStage: stage.cpStage
          ? setEntityFields(stage.cpStage, {
            isCriticalPathDeleted: isNotNil(deletedCriticalPathIndex[stage.cpStage.criticalPathId]),
          })
          : void 0,
      }))));

    // remove stages from the list of allowed stages
    this.setField(
      'criticalPathStages',
      this.value.criticalPathStages.filter((stage) => !deletedCriticalPathIndex[stage.criticalPathId]));

    // remove cost centers from the list of allowed cost centers
    this.setField(
      'criticalPathCostCenters',
      this.value.criticalPathCostCenters.filter((cc) =>
        !deletedCriticalPathIndex[cc.criticalPathId] && !deletedCostCenterIndex[cc.costCenterId]));
  }

  private updateStages(transform: IdentityFn<DrawRuleStage[]>): void {
    this.setField('stages', transform(this.value.stages));
  }

  private updateSelection(transform: IdentityFn<DrawRuleDetailsSelection>): void {
    this.setField('selection', transform(this.value.selection));
  }
}
